import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UpgradeSubscriptionModal from "../../components/modals/UpgradeSubscriptionModal";
import ThankYouModal from "../../components/modals/ThankYouModal";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import { adminAPI, clinicAppointmentsAPI } from "../../services/api";
import "../../styles/clinic-operations.css";

const ClinicOperationsHub = ({ clinicData, isOwner }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    appointments: null,
    outpatient: null,
    inpatient: null
  });
  const [displayCounts, setDisplayCounts] = useState({
    appointments: 0,
    outpatient: 0,
    inpatient: 0
  });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  const clinicName =
    clinicData?.clinic?.name_en || clinicData?.clinic?.name || t("clinicOperations.clinicFallback");
  const clinicId = clinicData?.clinic?.id;

  // Check new clinicsubscription table structure
  // Backend provides: clinic.clinic_subscription.plan or clinic.clinic_subscription_plan
  const subscriptionPlan =
    clinicData?.clinic?.clinic_subscription?.plan ||
    clinicData?.clinic?.clinic_subscription_plan;

  // Default to 'free' if subscription is null (old clinics or not migrated)
  const isFreePlan = !subscriptionPlan || subscriptionPlan === 'free';
  // Animate count up when counts change
  useEffect(() => {
    if (loadingCounts) return;

    const animateCount = (target, current, key) => {
      if (target === null || target === current) {
        setDisplayCounts(prev => ({ ...prev, [key]: target || 0 }));
        return;
      }

      const diff = target - current;
      const steps = Math.min(Math.abs(diff), 100); // Limit animation steps
      const stepDuration = 20; // 20ms per step
      const increment = diff / steps;

      let step = 0;
      const interval = setInterval(() => {
        step++;
        const newValue = Math.round(current + (increment * step));
        setDisplayCounts(prev => ({ ...prev, [key]: newValue }));

        if (step >= steps) {
          clearInterval(interval);
          setDisplayCounts(prev => ({ ...prev, [key]: target }));
        }
      }, stepDuration);
    };

    if (counts.appointments !== null) {
      animateCount(counts.appointments, displayCounts.appointments, 'appointments');
    }
    if (counts.outpatient !== null) {
      animateCount(counts.outpatient, displayCounts.outpatient, 'outpatient');
    }
    if (counts.inpatient !== null) {
      animateCount(counts.inpatient, displayCounts.inpatient, 'inpatient');
    }
  }, [counts, loadingCounts]);

  // Fetch counts
  const fetchTodayCounts = async (isManualRefresh = false) => {
    if (!clinicId) {
      setLoadingCounts(false);
      return;
    }

    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setLoadingCounts(true);
      }

      const token = localStorage.getItem("access_token");

      // Calculate local today date for accurate filtering
      const localNow = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
      const todayStr = localNow.toISOString().split('T')[0];

      // 1. Fetch total upcoming appointments (Today + Future)
      // This matches what users expect to see in the "Appointments" card
      let totalAppointments = 0;
      try {
        const appsResponse = await clinicAppointmentsAPI.searchAppointments({
          clinic_id: clinicId,
          date_from: todayStr,
          per_page: 1, // Minimize data transfer, we only need 'total'
          status: 'pending,confirmed,arrived,in_progress,upcoming' // Include 'upcoming' status
        });

        if (appsResponse.data?.status === 'success') {
          totalAppointments = appsResponse.data.data.pagination.total || 0;
        }
      } catch (e) {
        console.warn('Failed to fetch appointments count:', e);
      }

      // 2. Fetch current-day data for Outpatient/Inpatient (Active visits)
      // We keep using current-day endpoint for these as it likely returns active daily visits
      const endpoint = "/api/v1/clinic-appointments/current-day";
      const url = `${import.meta.env.VITE_API_URL}${endpoint}?clinic_id=${clinicId}`;

      let appointments = [];
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          appointments = data?.data?.appointments || [];
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.warn('Current-day API error:', errorData.message);
          appointments = [];
        }
      } catch (apiError) {
        console.warn('Error fetching current-day data:', apiError);
        appointments = [];
      }

      // Filter outpatient visits: only count active visits
      const outpatientOnly = appointments.filter(
        (appt) => {
          if (appt.type !== "outpatient_visit") return false;
          const status = (appt.status || "").toLowerCase();
          return !['completed', 'checkout', 'closed', 'canceled'].includes(status);
        }
      );

      // Filter inpatient visits: only count active visits
      const inpatientOnly = appointments.filter(
        (appt) => {
          if (appt.type !== "inpatient_visit") return false;
          const status = (appt.status || "").toLowerCase();
          return !['completed', 'checkout', 'closed', 'canceled'].includes(status);
        }
      );

      setCounts({
        appointments: totalAppointments,
        outpatient: outpatientOnly.length,
        inpatient: inpatientOnly.length
      });
    } catch (error) {
      console.error('Error fetching today counts:', error);
      setCounts({
        appointments: 0,
        outpatient: 0,
        inpatient: 0
      });
    } finally {
      setLoadingCounts(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTodayCounts();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchTodayCounts(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [clinicId]);

  // Add class to body to disable scrolling only on operations hub page
  useEffect(() => {
    document.body.classList.add('reception-page-active');

    return () => {
      document.body.classList.remove('reception-page-active');
    };
  }, []);

  const handleRefresh = () => {
    fetchTodayCounts(true);
  };

  const go = (path) => {
    // If path includes appointment-management and we have a clinicId, add clinicId to path
    let finalPath = path;
    if (clinicId && path.includes('/appointment-management')) {
      // Check if path already has clinicId
      if (!path.match(/\/medical\/clinic\/\d+\/appointment-management/)) {
        // Replace /medical/clinic/appointment-management with /medical/clinic/{clinicId}/appointment-management
        finalPath = path.replace('/medical/clinic/appointment-management', `/medical/clinic/${clinicId}/appointment-management`);
      }
    }
    navigate(finalPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const arrowIcon = i18n.dir() === "rtl" ? "←" : "→";

  const handleUpgradeInterest = async () => {
    try {
      if (!clinicId) {
        showErrorToast(t('upgradeSubscriptionModal.toasts.error'));
        return;
      }

      // Submit upgrade interest to backend
      await adminAPI.submitUpgradeInterest(clinicId);

      // Close upgrade modal and show thank you modal
      setShowUpgradeModal(false);
      setShowThankYouModal(true);
    } catch (error) {
      showErrorToast(t('upgradeSubscriptionModal.toasts.error'));
      throw error;
    }
  };

  const handleLockedCardClick = () => {
    if (isFreePlan) {
      setShowUpgradeModal(true);
    }
  };

  return (
    <div className="ops-hub" dir={i18n.dir()} lang={i18n.language}>
      {/* Grid */}
      <div className="ops-hub-grid-wrap">
        <div className="ops-hub-grid">
          {/* Emergency Queue */}
          <button
            onClick={() => go(`/medical/clinic/${clinicId}/appointment-management/emergency`)}
            className="ops-hub-card ops-hub-card-emergency"
          >
            <div className="ops-hub-card-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="ops-hub-card-title">
              {t("clinicOperations.cards.emergency.title", { clinicName })}
            </div>
            <div className="ops-hub-card-sub">
              {t("clinicOperations.cards.emergency.subtitle")}
            </div>
            <div className="ops-hub-card-arrow">{arrowIcon}</div>
          </button>

          {/* Appointments (owners only) */}
          {/* Appointments */}
          <button
            onClick={() => go(`/medical/clinic/${clinicId}/appointment-management/appointments`)}
            className="ops-hub-card ops-hub-card-appointments"
          >
            <div className="ops-hub-card-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div className="ops-hub-card-title">
              {t("clinicOperations.cards.appointments.title")}
              {loadingCounts ? (
                <span className="ops-hub-card-count-loading">
                  <span className="ops-hub-count-dot"></span>
                  <span className="ops-hub-count-dot"></span>
                  <span className="ops-hub-count-dot"></span>
                </span>
              ) : counts.appointments !== null ? (
                <span className="ops-hub-card-count">{displayCounts.appointments}</span>
              ) : null}
            </div>
            <div className="ops-hub-card-sub">
              {t("clinicOperations.cards.appointments.subtitle")}
            </div>
            <div className="ops-hub-card-arrow">{arrowIcon}</div>
          </button>

          {/* Outpatient Visits */}
          <div className={`ops-hub-card-wrapper ${isFreePlan ? 'ops-hub-card-locked' : ''}`}>
            <button
              onClick={() => isFreePlan ? handleLockedCardClick() : go(`/medical/clinic/${clinicId}/appointment-management/outpatient`)}
              className="ops-hub-card ops-hub-card-outpatient"
              disabled={false}
            >
              <div className="ops-hub-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="ops-hub-card-title">
                {t("clinicOperations.cards.outpatient.title")}
                {loadingCounts ? (
                  <span className="ops-hub-card-count-loading">
                    <span className="ops-hub-count-dot"></span>
                    <span className="ops-hub-count-dot"></span>
                    <span className="ops-hub-count-dot"></span>
                  </span>
                ) : counts.outpatient !== null ? (
                  <span className="ops-hub-card-count">{displayCounts.outpatient}</span>
                ) : null}
              </div>
              <div className="ops-hub-card-sub">
                {t("clinicOperations.cards.outpatient.subtitle")}
              </div>
              <div className="ops-hub-card-arrow">{arrowIcon}</div>
            </button>
            {isFreePlan && (
              <div className="ops-hub-card-lock-overlay">
                <div className="ops-hub-card-lock-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <div className="ops-hub-card-lock-text">
                  {t('upgradeSubscriptionModal.featureLocked')}
                </div>
              </div>
            )}
          </div>

          {/* Inpatient Visits */}
          <div className={`ops-hub-card-wrapper ${isFreePlan ? 'ops-hub-card-locked' : ''}`}>
            <button
              onClick={() => isFreePlan ? handleLockedCardClick() : go(`/medical/clinic/${clinicId}/appointment-management/inpatient`)}
              className="ops-hub-card ops-hub-card-inpatient"
              disabled={false}
            >
              <div className="ops-hub-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
              <div className="ops-hub-card-title">
                {t("clinicOperations.cards.inpatient.title")}
                {loadingCounts ? (
                  <span className="ops-hub-card-count-loading">
                    <span className="ops-hub-count-dot"></span>
                    <span className="ops-hub-count-dot"></span>
                    <span className="ops-hub-count-dot"></span>
                  </span>
                ) : counts.inpatient !== null ? (
                  <span className="ops-hub-card-count">{displayCounts.inpatient}</span>
                ) : null}
              </div>
              <div className="ops-hub-card-sub">
                {t("clinicOperations.cards.inpatient.subtitle")}
              </div>
              <div className="ops-hub-card-arrow">{arrowIcon}</div>
            </button>
            {isFreePlan && (
              <div className="ops-hub-card-lock-overlay">
                <div className="ops-hub-card-lock-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <div className="ops-hub-card-lock-text">
                  {t('upgradeSubscriptionModal.featureLocked')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <UpgradeSubscriptionModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSubmit={handleUpgradeInterest}
      />

      <ThankYouModal
        isOpen={showThankYouModal}
        onClose={() => setShowThankYouModal(false)}
      />
    </div>
  );
};

export default ClinicOperationsHub;


